// ===========================================
// useViewInstruments / useViewProgramLevels: picker lists for the page the user is on (#272)
// ===========================================
// The admin lists need class_config.read, so a plain teacher got a 403 and an empty instrument
// filter on the calendar and Classes page. A caller holding class_config.read reads the admin
// list; everyone else reads the calendar picker list, which teachers and parents may read.
// Exactly one of the two queries is enabled. Same shape as `useViewTerms`.
//
// The calendar program-level list holds ACTIVE levels only (the admin list holds all), so a
// teacher's legend has no entry for a retired level (plan D8).
//
// Caches: instrument and level edits invalidate the admin keys only. Anyone who can edit them
// holds class_config and reads those keys; the calendar keys (['calendar','instruments'],
// ['calendar','program-levels']) are read only by those who cannot edit them.

import type { UseQueryResult } from '@tanstack/react-query';
import { useInstruments, useProgramLevels } from './useAdmin';
import { useCalendarInstruments, useCalendarProgramLevels } from './useHybridBooking';
import { usePermissions } from './usePermissions';
import type { InstrumentOption, ProgramLevelOption } from '../api/hybridBooking.api';

export function useViewInstruments(): UseQueryResult<InstrumentOption[]> {
  const { can } = usePermissions();
  const canReadAdminList = can('class_config', 'read');
  const admin = useInstruments({ enabled: canReadAdminList });
  const view = useCalendarInstruments({ enabled: !canReadAdminList });
  return canReadAdminList ? admin : view;
}

export function useViewProgramLevels(): UseQueryResult<ProgramLevelOption[]> {
  const { can } = usePermissions();
  const canReadAdminList = can('class_config', 'read');
  const admin = useProgramLevels({ enabled: canReadAdminList });
  const view = useCalendarProgramLevels({ enabled: !canReadAdminList });
  return canReadAdminList ? admin : view;
}
